// SPA-side login redirect.
//
// **Flow (spec § Authentication & identity):**
//   1. Unauthenticated user visits any protected route (e.g. `/`).
//   2. App shell sees `whoAmI` returns 401 (cookie missing/expired).
//   3. App navigates to `/login` (this component).
//   4. This component issues a top-level browser navigation to the
//      BFF endpoint `/auth/login?return_to=<original-url>`.
//   5. BFF redirects to Cognito hosted UI.
//   6. Cognito redirects back to BFF `/auth/callback?code=...&state=...`.
//   7. BFF exchanges code → tokens, KMS-encrypts, writes
//      `bff.bff_sessions`, sets `sbp_session` cookie, redirects to
//      `return_to`.
//   8. SPA boots; `whoAmI` returns 200; app renders.
//
// **Why a top-level navigation, not `fetch('/auth/login')`:**
//   - The BFF responds with a 302 to Cognito's hosted UI. The browser
//     MUST navigate (not fetch) to follow that — the user needs to
//     interact with the hosted UI page.
//   - `window.location.assign` is the canonical way to trigger this
//     and preserves history (so the back button works).
//
// **`return_to` carriage (fn-45.4):** the SPA sends a RELATIVE
// path+search+hash (e.g. `/runs/abc?tab=approvals`), NOT an absolute
// href. The BFF's `isSafeReturnPath` accepts only same-origin relative
// paths (open-redirect protection) and reconstructs against its own
// configured origin on the post-login redirect — so sending an absolute
// URL would be rejected and silently fall back to `/`.
//
// **No state in the SPA component:** this is a fire-and-forget redirect.
// Rendering anything visible would flash a UI for ~50ms before the
// browser navigates; we render a minimal "Signing in..." string in
// case the navigation stalls (network blip).

import { useEffect } from 'react';

/**
 * Build the BFF login URL. Exported for tests.
 *
 * **Encoding:** the `return_to` is URL-encoded once. The BFF parses
 * it via `URLSearchParams` which decodes once — symmetry preserved.
 *
 * **`return_to` value (fn-45.4):** the mount site passes an explicit
 * RELATIVE `returnTo` (path+search+hash) so the BFF accepts it. The
 * `currentLocation.href` fallback is the legacy direct-navigation path —
 * the BFF rejects an absolute href and falls back to `/`, so callers
 * should always supply the relative `returnTo` in deployed mode.
 */
export function buildLoginUrl(opts: {
  readonly bffLoginPath?: string;
  readonly returnTo?: string;
  readonly currentLocation?: Pick<Location, 'href'>;
}): string {
  const path = opts.bffLoginPath ?? '/auth/login';
  const returnTo = opts.returnTo ?? opts.currentLocation?.href ?? '';
  if (returnTo.length === 0) return path;
  const params = new URLSearchParams({ return_to: returnTo });
  const separator = path.includes('?') ? '&' : '?';
  return `${path}${separator}${params.toString()}`;
}

export interface LoginRedirectProps {
  /**
   * BFF login endpoint. Defaults to `/auth/login` (dev, same-origin via
   * the Vite proxy). In deployed mode the mount site passes the ABSOLUTE
   * `${VITE_BFF_URL}/auth/login` so the navigation targets the BFF origin
   * (where the `__Host-` cookies live), not the SPA origin.
   */
  readonly bffLoginPath?: string;
  /**
   * Override location for tests. Defaults to `window.location`.
   * Function-shaped so tests can pass a constant.
   */
  readonly navigate?: (url: string) => void;
  /**
   * Explicit RELATIVE `return_to` (path+search+hash). The mount site
   * (`LocationAwareLoginRedirect`) passes this. The BFF accepts only
   * relative same-origin paths (open-redirect protection), so we send a
   * relative value — NOT an absolute href.
   */
  readonly returnTo?: string;
  /**
   * Override for the `return_to` source (absolute href). Used only when
   * `returnTo` is absent; defaults to `window.location.href`. Kept for the
   * direct-navigation fallback + tests.
   */
  readonly currentHref?: string;
}

/**
 * Renders a brief placeholder then redirects to the BFF login URL.
 */
export function LoginRedirect(props: LoginRedirectProps): React.ReactElement {
  useEffect(() => {
    const url = buildLoginUrl({
      ...(props.bffLoginPath !== undefined ? { bffLoginPath: props.bffLoginPath } : {}),
      // Prefer the explicit RELATIVE `returnTo` (deployed cross-origin
      // path); fall back to the absolute `currentHref` / `window.location`
      // only when the caller didn't supply one.
      ...(props.returnTo !== undefined
        ? { returnTo: props.returnTo }
        : {
            currentLocation:
              props.currentHref !== undefined ? { href: props.currentHref } : window.location,
          }),
    });
    const nav = props.navigate ?? ((u: string) => window.location.assign(u));
    nav(url);
  }, [props.bffLoginPath, props.navigate, props.returnTo, props.currentHref]);

  return (
    <main className="auth-redirect" aria-live="polite">
      <p>Signing in…</p>
    </main>
  );
}
